"use client";

import { Suspense, useState } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import {
  CalendarOff,
  Languages,
  MapPin,
  Quote,
  UserRound,
  UsersRound,
} from "lucide-react";
import { formatInTimeZone } from "date-fns-tz";
import { srLatn } from "date-fns/locale";

import SelectTherapist, { TherapistTeaser } from "./choose-therapist";
import UsuallyRespondsWithin from "./usually-responds-within";
import { buttonVariants } from "~/components/ui/button";
import { PriceDisplay } from "~/components/ui/price-display";
import { IconTextRow } from "~/components/ui/icon-text-row";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { yearsToString, roundToThreshold } from "~/lib/srpski";
import { type RouterOutput } from "~/server/api/root";
import { getMinimumClientAgePublicLabel } from "~/lib/minor-age";
import {
  isAnonymousReview,
  shouldBlurPublicReviewImage,
} from "~/lib/session-review-identity";
import { EUR } from "~/lib/currency";
import { TherapyRelationshipType } from "~/prisma/browser";
import { resolveTherapyTypeQuery } from "~/lib/therapy-type-query";
import { cn } from "~/lib/utils";

export type TherapistData = Omit<
  NonNullable<RouterOutput["therapistRouter"]["getTherapist"]>,
  | "practiceRevision"
  | "practiceUpdatedAt"
  | "practiceOrigin"
  | "practiceSourceHash"
  | "practiceMethodVersion"
  | "languageCodes"
  | "aiSummarySourceHash"
  | "recommendationExposureScore"
  | "recommendationExposureUpdatedAt"
  | "concerns"
  | "approaches"
  | "specialtyBadges"
>;

const DEFAULT_THERAPIST_TIMEZONE = "Europe/Belgrade";

function getValidTimeZone(timeZone: string) {
  try {
    new Intl.DateTimeFormat("en", { timeZone }).format();
    return timeZone;
  } catch {
    return DEFAULT_THERAPIST_TIMEZONE;
  }
}

export function formatTherapistBreakRange(
  startTime: Date,
  endTime: Date,
  timeZone: string,
) {
  const validTimeZone = getValidTimeZone(timeZone);
  const startDay = formatInTimeZone(startTime, validTimeZone, "yyyy-MM-dd");
  const endDay = formatInTimeZone(endTime, validTimeZone, "yyyy-MM-dd");
  if (startDay === endDay) {
    return formatInTimeZone(startTime, validTimeZone, "d. MMMM yyyy.", {
      locale: srLatn,
    });
  }

  const start = formatInTimeZone(startTime, validTimeZone, "d. MMM", {
    locale: srLatn,
  });
  const end = formatInTimeZone(endTime, validTimeZone, "d. MMM yyyy.", {
    locale: srLatn,
  });

  return `${start} – ${end}`;
}

/**
 * Prefer a positive review whose author chose to show their identity.
 * Within that pool, favor a natural-length quote, then rating and substance.
 */
export function getBestReviewQuote(
  reviews: TherapistData["user"]["sessionReviews"],
) {
  const withText = reviews.filter(
    (r) => r.review && r.review.trim().length > 30,
  );
  if (withText.length === 0) return null;

  const positivePublic = withText.filter(
    (r) => r.identityMode === "PUBLIC" && r.rating >= 4,
  );
  const preferred = positivePublic.length > 0 ? positivePublic : withText;
  const fitsNaturally = preferred.filter((r) => r.review!.trim().length <= 120);
  const pool = fitsNaturally.length > 0 ? fitsNaturally : preferred;
  const best = pool.sort((a, b) => {
    if (b.rating !== a.rating) return b.rating - a.rating;
    if (fitsNaturally.length > 0)
      return (b.review?.length ?? 0) - (a.review?.length ?? 0);
    return (a.review?.length ?? 0) - (b.review?.length ?? 0);
  })[0]!;

  return {
    text: best.review!.trim(),
    senderName: best.publicSender.name,
    senderImage: best.publicSender.image,
    identityMode: best.identityMode,
  };
}

function SidebarPriceFallback({ price }: { price: number }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <span className="text-primary text-3xl font-semibold md:text-4xl">
        {EUR.format(price)}
      </span>
      <span className="text-muted-foreground text-base md:text-lg">
        / sesija
      </span>
    </div>
  );
}

function SidebarBookingFallback({
  therapistSlug,
  isIncognito,
}: {
  therapistSlug: string;
  isIncognito: boolean;
}) {
  return (
    <Link
      className={buttonVariants({
        variant: "default",
        size: "lg",
        className: "w-full",
      })}
      href={`/terapeuti/${therapistSlug}/book`}
    >
      {isIncognito ? "Zakazivanje za postojeće klijente" : "Zakaži seansu"}
    </Link>
  );
}

export default function TherapistBookingSidebar(props: TherapistData) {
  const isOfferingCouplesTherapy = !!props.couplesTherapyPrice;
  const searchParams = useSearchParams();
  const initialTherapyType = isOfferingCouplesTherapy
    ? resolveTherapyTypeQuery({
        therapyType: searchParams.get("therapyType"),
        couples: searchParams.get("couples"),
      })
    : TherapyRelationshipType.INDIVIDUAL;
  const bestQuote = getBestReviewQuote(props.user.sessionReviews);
  const [therapyType, setTherapyType] =
    useState<TherapyRelationshipType>(initialTherapyType);
  const displayPrice =
    therapyType === TherapyRelationshipType.COUPLES
      ? (props.couplesTherapyPrice ?? props.defaultPrice)
      : props.defaultPrice;

  return (
    <div className="sticky top-24 hidden self-start lg:block">
      <div className="bg-card overflow-hidden rounded-2xl border shadow-xs">
        <div className="px-6 pt-6 pb-4">
          <Suspense fallback={<SidebarPriceFallback price={displayPrice} />}>
            <PriceDisplay
              originalPrice={displayPrice}
              showDailyBreakdown
              size="lg"
              checkOrgSponsorship={true}
            />
          </Suspense>
        </div>

        <div className="bg-muted/40 border-y px-6 py-5">
          <Suspense
            fallback={
              <SidebarBookingFallback
                therapistSlug={props.slug}
                isIncognito={props.isIncognito}
              />
            }
          >
            <SelectTherapist
              isIncognito={props.isIncognito}
              label="Zakaži seansu"
              variant="default"
              therapistSlug={props.slug}
              showCouplesOption={isOfferingCouplesTherapy}
              couplesPrice={props.couplesTherapyPrice ?? undefined}
              defaultPrice={props.defaultPrice}
              hideTeaser
              forceStacked
              offersIntroCall={props.offersIntroCall}
              onTherapyTypeChange={setTherapyType}
            />
          </Suspense>
        </div>

        <div className="space-y-4 px-6 py-5">
          <TherapistTeaser
            isIncognito={props.isIncognito}
            therapistSlug={props.slug}
            withScarcityMessage={false}
            variant="sidebar"
          />
          <UsuallyRespondsWithin
            therapistId={props.id}
            therapistUserId={props.userId}
            therapistName={props.user.name}
            variant="sidebar"
          />
        </div>

        {bestQuote && (
          <div className="border-t px-6 py-4">
            <IconTextRow icon={Quote} iconClassName="text-primary/30">
              <div className="min-w-0">
                <p className="text-muted-foreground line-clamp-3 text-sm leading-relaxed">
                  {bestQuote.text}
                </p>
                <div className="mt-3 flex items-center gap-2">
                  {isAnonymousReview(bestQuote.identityMode) ? (
                    <span className="border-brand/20 bg-brand/10 text-brand flex size-8 shrink-0 items-center justify-center rounded-full border">
                      <UserRound className="size-4" aria-hidden="true" />
                    </span>
                  ) : (
                    <Avatar className="size-8">
                      {bestQuote.senderImage && (
                        <AvatarImage
                          src={bestQuote.senderImage}
                          alt=""
                          className={cn(
                            shouldBlurPublicReviewImage(
                              bestQuote.identityMode,
                            ) && "blur-xs",
                          )}
                        />
                      )}
                      <AvatarFallback className="text-xs">
                        {bestQuote.senderName
                          .split(" ")
                          .map((part) => part[0])
                          .slice(0, 2)
                          .join("")
                          .toUpperCase() || "K"}
                      </AvatarFallback>
                    </Avatar>
                  )}
                  <p className="text-muted-foreground text-xs font-medium">
                    {bestQuote.senderName}
                  </p>
                </div>
              </div>
            </IconTextRow>
          </div>
        )}

        <div className="border-t px-6 py-5">
          <dl>
            <div className="grid grid-cols-3 divide-x border-b pb-5 text-center">
              <div className="px-1">
                <dt className="sr-only">Iskustvo</dt>
                <dd className="flex flex-col items-center">
                  <span className="text-foreground text-2xl leading-none font-semibold tabular-nums">
                    {props.yearsOfExperience}
                  </span>
                  <span className="text-muted-foreground mt-2 text-xs">
                    {yearsToString(props.yearsOfExperience ?? 0)} iskustva
                  </span>
                </dd>
              </div>
              <div className="px-1">
                <dt className="sr-only">Broj sesija</dt>
                <dd className="flex flex-col items-center">
                  <span className="text-foreground text-2xl leading-none font-semibold tabular-nums">
                    {roundToThreshold(props.sessionCount)}+
                  </span>
                  <span className="text-muted-foreground mt-2 text-xs">
                    sesija
                  </span>
                </dd>
              </div>
              <div className="px-1">
                <dt className="sr-only">Trajanje sesije</dt>
                <dd className="flex flex-col items-center">
                  <span className="text-foreground text-2xl leading-none font-semibold tabular-nums">
                    {therapyType === "COUPLES"
                      ? props.couplesSessionDuration
                      : props.individualSessionDuration}{" "}
                    <span className="text-base font-normal">min</span>
                  </span>
                  <span className="text-muted-foreground mt-2 text-xs">
                    {therapyType === "COUPLES" ? "partnerska" : "individualna"}{" "}
                    sesija
                  </span>
                </dd>
              </div>
            </div>
            <div className="grid gap-3 pt-5">
              {props.user.address?.city && (
                <IconTextRow icon={MapPin} definitionLabel="Lokacija">
                  <span className="text-foreground text-sm">
                    {props.user.address.city}, {props.user.address.country}
                  </span>
                </IconTextRow>
              )}
              <IconTextRow icon={Languages} definitionLabel="Jezici">
                <span className="text-foreground text-sm">
                  {props.languages.join(", ")}
                </span>
              </IconTextRow>
              <IconTextRow icon={UsersRound} definitionLabel="Uzrast klijenata">
                <span className="text-foreground text-sm">
                  {getMinimumClientAgePublicLabel(props.minimumClientAge)}
                </span>
              </IconTextRow>
            </div>
          </dl>
        </div>

        {props.TherapistBreak.length > 0 && (
          <div className="border-t px-6 py-4">
            <IconTextRow icon={CalendarOff}>
              <p className="text-muted-foreground text-xs">
                {props.TherapistBreak.length === 1
                  ? "Planirani odmor"
                  : "Planirani odmori"}
              </p>
              <div className="mt-1 space-y-1.5">
                {props.TherapistBreak.map((therapistBreak) => (
                  <p
                    key={therapistBreak.id}
                    className="text-foreground text-sm font-medium"
                  >
                    {formatTherapistBreakRange(
                      therapistBreak.startTime,
                      therapistBreak.endTime,
                      props.timezone,
                    )}
                  </p>
                ))}
              </div>
            </IconTextRow>
          </div>
        )}
      </div>
    </div>
  );
}
